import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface CampaignState {
  id: string | ""; //campaign Id
  category: string | ""; // Campaign Category
  name: string | ""; // Campaign Name
  use_case: string | ""; // Campaign Use Case
  branch: string | null; // Campaign Branch
  timezone: string | null; // Campaign Timezone
  expandedModalityId: string | null; // Curr Expanded Reachout Form
  start_time: string | null; // Campaign Start Time in UTC after launch from BE
  completed_at: string | null; // Campaign Completed Time in UTC from BE
  audience_data: {
    audience_count: number; // Audience Count from BE
    audience_data_link: string; // Audience Data Link from BE
    file_name: string; // Audience File Name from BE
    file_key: string; // Audience File Key
    file_id: string; // Audience File ID from BE
    valid_audience_count?: number; // Valid Audience Count from BE
    invalid_audience_count?: number; // Invalid Audience Count from BE
    segment_id?: string | null; // Segment ID if audience came from a segment
  } | null;
  modality_info: {
    id: string; // Modality Id on Save from BE
    channel: string; // Call, Email, Whatsapp Enum
    sender_email?: string; // Sender email for email reachouts
    sender_name?: string; // Sender name for email reachouts
    template: {
      id: string; // Template Id
      name: string; //Template Name  FROM BE
    };
    start_date: Date | string; // Start Date for reach out
    start_time: string; // Start Time for reach out
    run_time: string; // run time in UTC
    is_saved: boolean; // Whether the reachout is saved or not
    is_edited: boolean; // Whether the reachout is edited or not
  }[];
  created_at: string | null; // Created At in UTC from BE
  language: {
    name: string;
    language: string;
  };
  modality: string | null;
  segment_data: {
    id: string;
    name: string;
    size: number;
    type: string;
  } | null;
  campaign_ai_type: "ai" | "extraction_only";
  completedSteps: {
    category: boolean;
    details: boolean;
    reachouts: boolean;
    reachoutConfig: boolean;
    contacts: boolean;
  };
}

const initialState: CampaignState = {
  id: "",
  category: "",
  name: "",
  use_case: "",
  branch: null,
  timezone: "",
  expandedModalityId: null,
  start_time: null,
  completed_at: null,
  audience_data: {
    audience_data_link: "",
    audience_count: 0,
    file_name: "",
    file_key: "",
    file_id: "",
  },
  created_at: null,
  modality_info: [],
  language: {
    name: "",
    language: "",
  },
  modality: null,
  segment_data: null,
  campaign_ai_type: "ai",
  completedSteps: {
    category: false,
    details: false,
    reachouts: false,
    reachoutConfig: false,
    contacts: false,
  },
};

export const createCampaignSlice = createSlice({
  name: "v2Campaign",
  initialState,
  reducers: {
    updateCampaign: (state, action: PayloadAction<Partial<CampaignState>>) => {
      return { ...state, ...action.payload };
    },
    updateModalityInfo: (
      state,
      action: PayloadAction<{
        id: string;
        data: Partial<CampaignState["modality_info"][0]>;
      }>
    ) => {
      const { id, data } = action.payload;
      const index = state.modality_info.findIndex((item) => item.id === id);
      if (index !== -1) {
        state.modality_info[index] = { ...state.modality_info[index], ...data };
      }
    },
    addModalityInfo: (
      state,
      action: PayloadAction<CampaignState["modality_info"][0]>
    ) => {
      state.modality_info.push(action.payload);
      state.expandedModalityId = action.payload.id || null;
    },
    removeModalityInfo: (state, action: PayloadAction<string>) => {
      state.modality_info = state.modality_info.filter(
        (item) => item.id !== action.payload
      );
      if (state.expandedModalityId === action.payload) {
        state.expandedModalityId = null;
      }
    },
    setExpandedModality: (state, action: PayloadAction<string | null>) => {
      state.expandedModalityId = action.payload;
    },
    toggleModalityExpansion: (state, action: PayloadAction<string>) => {
      const modalityId = action.payload;
      if (state.expandedModalityId === modalityId) {
        state.expandedModalityId = null;
      } else {
        state.expandedModalityId = modalityId;
      }
    },
    markStepCompleted: (
      state,
      action: PayloadAction<keyof CampaignState["completedSteps"]>
    ) => {
      state.completedSteps[action.payload] = true;
    },
    invalidateStepsAfter: (
      state,
      action: PayloadAction<keyof CampaignState["completedSteps"]>
    ) => {
      const stepOrder: (keyof CampaignState["completedSteps"])[] = [
        "category",
        "details",
        "reachouts",
        "reachoutConfig",
        "contacts",
      ];

      const stepIndex = stepOrder.indexOf(action.payload);

      for (let i = stepIndex + 1; i < stepOrder.length; i++) {
        state.completedSteps[stepOrder[i]] = false;
      }
    },

    resetCampaign: () => {
      return initialState;
    },
  },
});

export const {
  updateCampaign,
  updateModalityInfo,
  addModalityInfo,
  removeModalityInfo,
  setExpandedModality,
  toggleModalityExpansion,
  markStepCompleted,
  invalidateStepsAfter,
  resetCampaign,
} = createCampaignSlice.actions;

export default createCampaignSlice.reducer;
